import React, { useRef, useState, useCallback, useEffect } from 'react';
import Map, { Source, Layer, NavigationControl, ScaleControl, Popup, Marker } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'mapbox-gl/dist/mapbox-gl.css';
import envValues from '../../../../enviornment';
import { getCookies } from '../../../../hooks/useCookies';
import { REACT_APP_API_ADMIN_POIS, REACT_APP_API_ADMIN_BATHYMETRY } from '../../../../redux/apiConstants';
import WaypointMarker from './WaypointMarker';
import PoiMarker from './PoiMarker';
import { getStopNumber } from '../constants/stopLabels';
import { getPoiIconSvg, getPoiMeta, getPoiTypeColor, svgToDataUrl } from '../constants/poiIcons';
import styles from '../CreateRoute.module.scss';
import { formatCoordinates, parseCoordinatePair } from '../../../../utils/coordinates';

const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || envValues.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

const DEFAULT_VIEW = { longitude: 0, latitude: 30, zoom: 4 };
const ROUTE_FOCUS_PADDING = { top: 80, bottom: 80, left: 80, right: 420 };

const normalizeRouteCoords = (coords) =>
  (Array.isArray(coords) ? coords : []).filter(
    (c) => Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]),
  );

/** Fallback camera when the map instance is not ready yet */
const estimateCameraForCoords = (coords) => {
  const valid = normalizeRouteCoords(coords);
  if (!valid.length) return null;
  if (valid.length === 1) {
    const [lng, lat] = valid[0];
    return { longitude: lng, latitude: lat, zoom: 11 };
  }

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  valid.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });

  const longitude = (minLng + maxLng) / 2;
  const latitude = (minLat + maxLat) / 2;
  const span = Math.max(Math.max(maxLng - minLng, 0.01), Math.max(maxLat - minLat, 0.01));
  const zoom = Math.min(14, Math.max(4, Math.log2(180 / span)));

  return { longitude, latitude, zoom };
};
const POI_RADIUS_NM = 10;
const DEPTH_LAYER_ID = 'admin-depth-grid';
const MAX_DEPTH_SPAN_DEG = 5;
const DEPTH_FETCH_DEBOUNCE_MS = 600;

// ─── Route line layer builders ────────────────────────────────────────────────
const makeHitLayer  = (id) => ({
  id: `${id}-hit`,
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': '#000', 'line-width': 20, 'line-opacity': 0 },
});
const makeCasingLayer = (id) => ({
  id: `${id}-casing`,
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': '#ffffff', 'line-width': 8, 'line-opacity': 0.5 },
});
const makeLineLayer = (id, color, isGenerated) => ({
  id: `${id}-line`,
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: {
    'line-color': isGenerated ? '#39979d' : color,
    'line-width': 3.5,
  },
});

/** Canvas (non-active) route line — slightly thinner and semi-transparent */
const makeCanvasLineLayer = (id, color) => ({
  id: `${id}-canvas-line`,
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': color, 'line-width': 2.5, 'line-opacity': 0.65 },
});
const makeCanvasCasingLayer = (id) => ({
  id: `${id}-canvas-casing`,
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': '#fff', 'line-width': 6, 'line-opacity': 0.3 },
});

// ─── Nearest-segment finder (coordinate space) ────────────────────────────────
/**
 * Projects [lng, lat] onto each segment of `wps` and returns the closest one.
 * @param {[number,number][]} wps
 * @param {number} lng
 * @param {number} lat
 * @returns {{ afterIndex: number, coord: [number,number] } | null}
 */
const findNearestSegment = (wps, lng, lat) => {
  let minDist = Infinity;
  let bestIdx = -1;
  let bestCoord = [lng, lat];

  for (let i = 0; i < wps.length - 1; i += 1) {
    const [x1, y1] = wps[i];
    const [x2, y2] = wps[i + 1];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-14) continue; // eslint-disable-line no-continue
    const t = Math.max(0, Math.min(1, ((lng - x1) * dx + (lat - y1) * dy) / lenSq));
    const projX = x1 + t * dx;
    const projY = y1 + t * dy;
    const dist = (lng - projX) ** 2 + (lat - projY) ** 2;
    if (dist < minDist) {
      minDist = dist;
      bestIdx = i;
      bestCoord = [+projX.toFixed(6), +projY.toFixed(6)];
    }
  }
  return bestIdx >= 0 ? { afterIndex: bestIdx, coord: bestCoord } : null;
};

// ─── ENC depth grid layer (NOAA soundings from S3) ───────────────────────────
const DEPTH_CIRCLE_LAYER = {
  id: DEPTH_LAYER_ID,
  type: 'circle',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 2, 10, 4, 14, 7, 18, 11],
    'circle-color': [
      'match', ['get', 'riskLevel'],
      'HIGH', '#d32f2f',
      'MEDIUM', '#f57c00',
      'SAFE', '#1565c0',
      '#78909c',
    ],
    'circle-opacity': 0.72,
    'circle-stroke-width': 0,
  },
};


/** Fetch place suggestions from Mapbox Geocoding API */
const fetchSuggestions = async (query) => {
  const encoded = encodeURIComponent(query.trim());
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${MAPBOX_TOKEN}&limit=6&types=place,locality,neighborhood,address,poi,region,country`;
  const res = await fetch(url);
  if (!res.ok) return [];
  return (await res.json()).features || [];
};

/** Fetch POIs from admin API — generous timeout to cover Overpass cold-cache sync (~20 s) */
const fetchPOIs = async (lat, lon) => {
  const token = getCookies('Token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await axios.get(`${REACT_APP_API_ADMIN_POIS}/search`, {
    params: { lat, lon, radiusNm: POI_RADIUS_NM },
    headers,
    timeout: 35000,   // Overpass cold-cache sync can take up to ~22 s
  });
  return res?.data?.data?.pois || [];
};

/** Fetch ENC depth soundings for the current map viewport */
const fetchDepthGrid = async (bounds, zoomLevel) => {
  const token = getCookies('Token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await axios.get(`${REACT_APP_API_ADMIN_BATHYMETRY}/grid`, {
    params: {
      minLat: bounds.minLat,
      maxLat: bounds.maxLat,
      minLon: bounds.minLon,
      maxLon: bounds.maxLon,
      zoomLevel,
    },
    headers,
    timeout: 45000,
  });
  return res?.data?.data || null;
};

/** Convert depth points to GeoJSON FeatureCollection */
const depthPointsToGeoJson = (points) => ({
  type: 'FeatureCollection',
  features: (points || []).map((p) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [p.longitude, p.latitude],
    },
    properties: {
      depth: p.depth,
      riskLevel: p.riskLevel,
    },
  })),
});

/**
 * @param {{
 *   waypoints: [number, number][],
 *   waypointLabels: string[],
 *   selectedIndex: number | null,
 *   geoJson: object,
 *   flyToCoord: [number, number] | null,
 *   routeMapFocus: { coords: [number, number][], at: number } | null,
 *   onMapClick: (e: any) => void,
 *   onInsertWaypoint: (afterIndex: number, coord: [number, number]) => void,
 *   onWaypointDrag: (index: number, e: any) => void,
 *   onWaypointClick: (index: number) => void,
 *   onStartMarkerDrag: (coord: [number, number]) => void,
 *   onDestMarkerDrag: (coord: [number, number]) => void,
 * }} props
 */
const RouteMap = ({ waypoints, waypointLabels = [], selectedIndex, drawFromIndex, activeRouteColor, canvasRoutes = [], geoJson, flyToCoord, routeMapFocus, isRouteGenerated, onMapClick, onInsertWaypoint, onWaypointDrag, onWaypointClick, onCancelDrawFrom, startMarker = null, destMarker = null, onStartMarkerDrag, onDestMarkerDrag }) => {
  const mapRef        = useRef(null);
  const searchWrapRef = useRef(null);
  const inputRef      = useRef(null);
  const debounceRef   = useRef(null);
  const depthDebounceRef = useRef(null);
  const skipRef       = useRef(false);
  // Stable ref so mouse-move handler never goes stale
  const waypointsRef  = useRef(waypoints);
  useEffect(() => { waypointsRef.current = waypoints; }, [waypoints]);

  const pendingRouteFocusRef = useRef(null);

  const applyRouteMapFocus = useCallback((coords) => {
    const valid = normalizeRouteCoords(coords);
    if (!valid.length) return;

    let nextView = estimateCameraForCoords(valid);
    const map = mapRef.current?.getMap();

    if (map) {
      try {
        if (valid.length === 1) {
          const [lng, lat] = valid[0];
          nextView = { longitude: lng, latitude: lat, zoom: 11 };
        } else {
          const bounds = new mapboxgl.LngLatBounds();
          valid.forEach(([lng, lat]) => bounds.extend([lng, lat]));
          const camera = map.cameraForBounds(bounds, {
            padding: ROUTE_FOCUS_PADDING,
            maxZoom: 14,
          });
          if (camera?.center) {
            nextView = {
              longitude: camera.center.lng,
              latitude: camera.center.lat,
              zoom: camera.zoom,
            };
          }
        }
      } catch (_) {
        /* keep fallback camera */
      }
    }

    setViewState((prev) => ({ ...prev, ...nextView }));
  }, []);

  const [viewState, setViewState]       = useState(DEFAULT_VIEW);
  const [query, setQuery]               = useState('');
  const [suggestions, setSuggestions]   = useState([]);
  const [isSearching, setIsSearching]   = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // POI state
  const [pois, setPois]               = useState([]);
  const [showPois, setShowPois]       = useState(true);
  const [isFetchingPois, setIsFetchingPois] = useState(false);
  const [selectedPoi, setSelectedPoi] = useState(null);

  // Depth grid state (NOAA ENC soundings from S3)
  const [showDepth, setShowDepth]               = useState(true);
  const [depthGeoJson, setDepthGeoJson]         = useState({ type: 'FeatureCollection', features: [] });
  const [depthPointCount, setDepthPointCount]   = useState(0);
  const [isFetchingDepth, setIsFetchingDepth]   = useState(false);
  const [depthHint, setDepthHint]               = useState('');

  // Map style toggle — street / satellite
  const [isSatellite, setIsSatellite] = useState(false);

  // Mid-segment hover state — shows ghost "+" marker for inserting intermediate waypoints
  const [midHover, setMidHover] = useState(null); // { afterIndex, coord } | null

  // ─── Escape key cancels draw-from mode ───────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && drawFromIndex !== null) onCancelDrawFrom?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [drawFromIndex, onCancelDrawFrom]);

  // ─── Close search dropdown on outside click ───────────────────────────────
  useEffect(() => {
    const handleOutside = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // ─── Debounced geocoding ──────────────────────────────────────────────────
  const flyToLocation = useCallback(async (lng, lat, displayName) => {
    clearTimeout(debounceRef.current);
    skipRef.current = true;
    setQuery(displayName);
    setSuggestions([]);
    setDropdownOpen(false);
    setIsSearching(false);
    setSelectedPoi(null);
    setPois([]);
    inputRef.current?.blur();

    mapRef.current?.getMap()?.flyTo({ center: [lng, lat], zoom: 9, duration: 1200 });

    setIsFetchingPois(true);
    try {
      const result = await fetchPOIs(lat, lng);
      setPois(result);
      setShowPois(true);
      if (result.length === 0) {
        toast.info(`No marine POIs found within ${POI_RADIUS_NM} NM of this location.`);
      } else {
        toast.success(`${result.length} POI${result.length !== 1 ? 's' : ''} loaded near ${displayName || 'this location'}.`);
      }
    } catch (err) {
      console.error('[POI fetch error]', err?.response?.data || err?.message || err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to load nearby POIs.';
      toast.error(`POI fetch failed: ${msg}`);
      setPois([]);
    } finally {
      setIsFetchingPois(false);
    }
  }, []);

  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return undefined; }
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setDropdownOpen(false);
      setIsSearching(false);
      return undefined;
    }

    const parsed = parseCoordinatePair(trimmed);
    if (parsed) {
      const [lng, lat] = parsed;
      flyToLocation(lng, lat, formatCoordinates(parsed));
      return undefined;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await fetchSuggestions(trimmed);
        setSuggestions(results);
        setDropdownOpen(results.length > 0);
      } catch (_) {
        setSuggestions([]);
        setDropdownOpen(false);
      } finally {
        setIsSearching(false);
      }
    }, 320);
    return () => clearTimeout(debounceRef.current);
  }, [query, flyToLocation]);

  // ─── Select place → fly + fetch POIs ─────────────────────────────────────
  const handleSelectPlace = useCallback(async (feature) => {
    const [lng, lat] = feature.center;
    await flyToLocation(lng, lat, feature.place_name || feature.text || '');
  }, [flyToLocation]);

  const handleClearSearch = useCallback(() => {
    skipRef.current = true;
    setQuery('');
    setSuggestions([]);
    setDropdownOpen(false);
    inputRef.current?.focus();
  }, []);

  // ─── Mouse move — detect hover over route line ───────────────────────────
  const handleMouseMove = useCallback((e) => {
    const wps = waypointsRef.current;
    if (wps.length < 2) { setMidHover(null); return; }
    const map = mapRef.current?.getMap();
    if (!map) return;
    const hits = map.queryRenderedFeatures(e.point, { layers: ['admin-route-hit'] });
    if (hits.length > 0) {
      const { lng, lat } = e.lngLat;
      const seg = findNearestSegment(wps, lng, lat);
      setMidHover(seg);
    } else {
      setMidHover(null);
    }
  }, []);

  const handleMouseLeave = useCallback(() => setMidHover(null), []);

  const handleStartMarkerDragEnd = useCallback((e) => {
    if (!onStartMarkerDrag) return;
    const { lng, lat } = e.lngLat;
    onStartMarkerDrag([parseFloat(lng.toFixed(6)), parseFloat(lat.toFixed(6))]);
  }, [onStartMarkerDrag]);

  const handleDestMarkerDragEnd = useCallback((e) => {
    if (!onDestMarkerDrag) return;
    const { lng, lat } = e.lngLat;
    onDestMarkerDrag([parseFloat(lng.toFixed(6)), parseFloat(lat.toFixed(6))]);
  }, [onDestMarkerDrag]);

  const handlePoiSelect = useCallback((poi) => {
    const coord = poi.location?.coordinates || [0, 0];
    setSelectedPoi({
      lng: coord[0],
      lat: coord[1],
      id: poi.id || poi._id,
      name: poi.name || 'Unnamed',
      type: poi.type || 'marina',
      description: poi.description || '',
      phone: poi.phone || '',
      website: poi.website || '',
      amenities: Array.isArray(poi.amenities) ? poi.amenities : [],
    });
  }, []);

  // ─── Click: insert intermediate → append ─────────────────────────────────
  const handleMapClick = useCallback((e) => {
    setSelectedPoi(null);

    // Insert intermediate waypoint when hovering a segment
    if (midHover) {
      onInsertWaypoint(midHover.afterIndex, midHover.coord);
      setMidHover(null);
      return;
    }

    // Default — append waypoint at end
    onMapClick(e);
  }, [onMapClick, onInsertWaypoint, midHover]);

  // ─── ENC depth grid — fetch for current viewport ─────────────────────────
  const loadDepthForViewport = useCallback(async () => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const bounds = map.getBounds();
    const minLat = bounds.getSouth();
    const maxLat = bounds.getNorth();
    const minLon = bounds.getWest();
    const maxLon = bounds.getEast();
    const latSpan = maxLat - minLat;
    const lonSpan = maxLon - minLon;

    if (latSpan > MAX_DEPTH_SPAN_DEG || lonSpan > MAX_DEPTH_SPAN_DEG) {
      setDepthHint('Zoom in to load depth soundings');
      setDepthGeoJson({ type: 'FeatureCollection', features: [] });
      setDepthPointCount(0);
      setIsFetchingDepth(false);
      return;
    }

    setDepthHint('');
    setIsFetchingDepth(true);
    try {
      const zoomLevel = Math.round(map.getZoom());
      const data = await fetchDepthGrid({ minLat, maxLat, minLon, maxLon }, zoomLevel);
      const points = data?.points || [];
      setDepthGeoJson(depthPointsToGeoJson(points));
      setDepthPointCount(data?.count ?? points.length);
      if (points.length === 0) {
        setDepthHint('No ENC soundings in this area');
      }
    } catch (err) {
      console.error('[depth grid fetch error]', err?.response?.data || err?.message || err);
      setDepthGeoJson({ type: 'FeatureCollection', features: [] });
      setDepthPointCount(0);
      setDepthHint('Failed to load depth data');
    } finally {
      setIsFetchingDepth(false);
    }
  }, []);

  const scheduleDepthFetch = useCallback(() => {
    clearTimeout(depthDebounceRef.current);
    depthDebounceRef.current = setTimeout(loadDepthForViewport, DEPTH_FETCH_DEBOUNCE_MS);
  }, [loadDepthForViewport]);

  useEffect(() => () => clearTimeout(depthDebounceRef.current), []);

  useEffect(() => {
    if (!showDepth) {
      setDepthGeoJson({ type: 'FeatureCollection', features: [] });
      setDepthPointCount(0);
      setDepthHint('');
      clearTimeout(depthDebounceRef.current);
      return;
    }
    scheduleDepthFetch();
  }, [showDepth, scheduleDepthFetch]);

  const handleMoveEnd = useCallback(() => {
    if (showDepth) scheduleDepthFetch();
  }, [showDepth, scheduleDepthFetch]);

  // ─── Map setup on load ────────────────────────────────────────────────────
  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    try { map.setProjection('globe'); } catch (_) { /* ignore */ }
    try {
      map.setFog({
        color: 'rgb(186, 210, 235)',
        'high-color': 'rgb(36, 92, 223)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(11, 11, 25)',
        'star-intensity': 0.6,
      });
    } catch (_) { /* ignore */ }
    const container = map.getContainer();
    const hide = () => {
      container?.querySelector('.mapboxgl-ctrl-logo')?.style.setProperty('display', 'none');
      container?.querySelector('.mapboxgl-ctrl-attrib')?.style.setProperty('display', 'none');
    };
    hide();
    setTimeout(hide, 300);
    if (showDepth) scheduleDepthFetch();

    if (pendingRouteFocusRef.current) {
      applyRouteMapFocus(pendingRouteFocusRef.current);
      pendingRouteFocusRef.current = null;
    }
  }, [showDepth, scheduleDepthFetch, applyRouteMapFocus]);

  // ─── Fly to coord when changed from panel stop search ────────────────────
  useEffect(() => {
    if (!flyToCoord) return;
    const [lng, lat] = flyToCoord;
    setViewState((prev) => ({ ...prev, longitude: lng, latitude: lat, zoom: 11 }));
  }, [flyToCoord]);

  // ─── Fit map to loaded saved route (controlled viewState) ────────────────
  useEffect(() => {
    const coords = routeMapFocus?.coords;
    if (!coords?.length) return undefined;

    pendingRouteFocusRef.current = coords;
    applyRouteMapFocus(coords);

    return undefined;
  }, [routeMapFocus?.at, applyRouteMapFocus]);

  // ─── Keep globe projection on style reload ────────────────────────────────
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return undefined;
    const apply = () => { try { map.setProjection('globe'); } catch (_) { /* ignore */ } };
    map.on('styledata', apply);
    return () => map.off('styledata', apply);
  }, []);

  if (!MAPBOX_TOKEN) {
    return (
      <div className={styles.noToken}>
        <span>⚠</span>
        Mapbox token not configured. Add <code>REACT_APP_MAPBOX_ACCESS_TOKEN</code> to your <code>.env</code> file.
      </div>
    );
  }

  const meta = selectedPoi ? getPoiMeta(selectedPoi.type) : null;
  const selectedPoiIconSrc = selectedPoi ? svgToDataUrl(getPoiIconSvg(selectedPoi.type)) : null;
  const selectedPoiId = selectedPoi?.id;

  return (
    <div className={styles.mapContainer}>

      {/* ── Search bar ──────────────────────────────────────────────────── */}
      <div className={styles.searchOverlay} ref={searchWrapRef}>
        <div className={styles.searchBox}>
          <span className={styles.searchIconSvg} aria-hidden="true">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <circle cx="10.5" cy="10.5" r="7.5" stroke="currentColor" strokeWidth="2" />
              <path d="M16 16l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <input
            ref={inputRef}
            type="search"
            className={styles.searchInput}
            placeholder="Search city, port, or location…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setDropdownOpen(true); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setDropdownOpen(false); return; }
              if (e.key === 'Enter') {
                e.preventDefault();
                const parsed = parseCoordinatePair(query.trim());
                if (parsed) {
                  const [lng, lat] = parsed;
                  flyToLocation(lng, lat, formatCoordinates(parsed));
                } else if (suggestions[0]) {
                  handleSelectPlace(suggestions[0]);
                }
              }
            }}
            aria-label="Search location"
            aria-autocomplete="list"
            aria-controls="route-search-list"
          />
          {isSearching && <span className={styles.searchSpinner} aria-hidden="true" />}
          {query && !isSearching && (
            <button type="button" className={styles.searchClear} onClick={handleClearSearch} aria-label="Clear search">✕</button>
          )}
        </div>

        {dropdownOpen && suggestions.length > 0 && (
          <ul id="route-search-list" className={styles.searchDropdown} role="listbox" aria-label="Search results">
            {suggestions.map((feature) => (
              <li key={feature.id} role="option" aria-selected={false}>
                <button type="button" className={styles.searchSuggestion} onClick={() => handleSelectPlace(feature)}>
                  <span className={styles.suggestionIcon} aria-hidden="true">📍</span>
                  <span className={styles.suggestionText}>
                    <span className={styles.suggestionMain}>{feature.text}</span>
                    <span className={styles.suggestionSub}>{feature.place_name}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Generated route badge ───────────────────────────────────── */}
      {isRouteGenerated && (
        <div className={styles.generatedBadge} aria-label="Route generated via Mapbox Directions">
          ⚡ Generated Route
        </div>
      )}

      {/* ── Depth grid toolbar ───────────────────────────────────────── */}
      <div className={styles.depthToolbar}>
        {isFetchingDepth ? (
          <>
            <span className={styles.depthLoadingDot} aria-hidden="true" />
            <span className={styles.depthCount}>Loading depth soundings…</span>
          </>
        ) : (
          <>
            <span className={styles.depthCount}>
              {depthPointCount > 0
                ? `${depthPointCount.toLocaleString()} depth point${depthPointCount !== 1 ? 's' : ''}`
                : depthHint || (showDepth ? 'Pan/zoom to load depth' : 'Depth hidden')}
            </span>
            {depthPointCount > 0 && showDepth && (
              <span className={styles.depthLegend} aria-label="Depth legend">
                <span className={styles.depthLegendItem}><i className={styles.depthSwatchHigh} /> &lt;3m</span>
                <span className={styles.depthLegendItem}><i className={styles.depthSwatchMed} /> 3–10m</span>
                <span className={styles.depthLegendItem}><i className={styles.depthSwatchSafe} /> ≥10m</span>
              </span>
            )}
          </>
        )}
        <button
          type="button"
          className={`${styles.depthToggleBtn} ${showDepth ? styles.depthToggleActive : ''}`}
          onClick={() => setShowDepth((p) => !p)}
          aria-label={showDepth ? 'Hide depth grid' : 'Show depth grid'}
        >
          {showDepth ? '👁 Hide Depth' : '👁 Show Depth'}
        </button>
      </div>

      {/* ── POI toolbar: loading status OR count+toggle ──────────────── */}
      {isFetchingPois && (
        <div className={styles.poiToolbar}>
          <span className={styles.poiLoadingDot} aria-hidden="true" />
          <span className={styles.poiCount}>Searching POIs nearby…</span>
        </div>
      )}
      {!isFetchingPois && pois.length > 0 && (
        <div className={styles.poiToolbar}>
          <span className={styles.poiCount}>
            {pois.length} POI{pois.length !== 1 ? 's' : ''} within {POI_RADIUS_NM} NM
          </span>
          <button
            type="button"
            className={`${styles.poiToggleBtn} ${showPois ? styles.poiToggleActive : ''}`}
            onClick={() => { setShowPois((p) => !p); setSelectedPoi(null); }}
            aria-label={showPois ? 'Hide POIs' : 'Show POIs'}
          >
            {showPois ? '👁 Hide POIs' : '👁 Show POIs'}
          </button>
        </div>
      )}

      {/* ── Satellite toggle ────────────────────────────────────────────── */}
      <button
        type="button"
        className={`${styles.satelliteToggleBtn} ${isSatellite ? styles.satelliteToggleActive : ''}`}
        onClick={() => setIsSatellite((p) => !p)}
        aria-label={isSatellite ? 'Switch to map view' : 'Switch to satellite view'}
        title={isSatellite ? 'Map view' : 'Satellite view'}
      >
        {isSatellite ? (
          <>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
              <line x1="9" y1="3" x2="9" y2="18" />
              <line x1="15" y1="6" x2="15" y2="21" />
            </svg>
            Map
          </>
        ) : (
          <>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            Satellite
          </>
        )}
      </button>

      {/* ── Mapbox map ──────────────────────────────────────────────────── */}
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        {...viewState}
        onMove={(e) => setViewState(e.viewState)}
        onMoveEnd={handleMoveEnd}
        style={{ width: '100%', height: '100%' }}
        mapStyle={isSatellite ? 'mapbox://styles/mapbox/satellite-streets-v12' : 'mapbox://styles/mapbox/outdoors-v12'}
        attributionControl={false}
        onClick={handleMapClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onLoad={handleMapLoad}
        cursor={midHover ? 'cell' : 'crosshair'}
        interactiveLayerIds={[
          ...(waypoints.length >= 2 ? ['admin-route-hit'] : []),
        ]}
        // admin-route-hit matches the id built by makeHitLayer('admin-route')
        projection="globe"
      >
        <NavigationControl position="top-right" showCompass />
        <ScaleControl position="bottom-left" unit="metric" />

        {/* ── ENC depth soundings (S3 NOAA cells) ─────────────── */}
        {showDepth && depthGeoJson.features.length > 0 && (
          <Source id="admin-depth-src" type="geojson" data={depthGeoJson}>
            <Layer {...DEPTH_CIRCLE_LAYER} />
          </Source>
        )}

        {/* ── Canvas (non-active) routes ──────────────────────── */}
        {canvasRoutes.map((cr) => {
          if (!cr.waypoints || cr.waypoints.length < 2) return null;
          const coords = cr.generatedCoordinates || cr.waypoints;
          const data = {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: coords },
            properties: {},
          };
          return (
            <Source key={cr.id} id={`cr-${cr.id}`} type="geojson" data={data}>
              <Layer {...makeCanvasCasingLayer(cr.id)} />
              <Layer {...makeCanvasLineLayer(cr.id, cr.color)} />
            </Source>
          );
        })}

        {/* ── Active route line + invisible hit area ──────────── */}
        {waypoints.length >= 2 && (
          <Source id="admin-route-src" type="geojson" data={geoJson}>
            <Layer {...makeHitLayer('admin-route')} />
            <Layer {...makeCasingLayer('admin-route')} />
            <Layer {...makeLineLayer('admin-route', activeRouteColor, isRouteGenerated)} />
          </Source>
        )}

        {/* Route start/end markers removed — waypoint markers handle endpoint display */}

        {/* POI markers with type-specific icons */}
        {showPois && pois.map((poi) => (
          <PoiMarker
            key={poi.id || poi._id}
            poi={poi}
            isSelected={selectedPoiId === (poi.id || poi._id)}
            onSelect={handlePoiSelect}
          />
        ))}

        {/* Waypoint markers */}
        {waypoints.map((coord, index) => (
          <WaypointMarker
            /* eslint-disable-next-line react/no-array-index-key */
            key={`wp-${index}`}
            coordinate={coord}
            index={index}
            total={waypoints.length}
            label={waypointLabels[index] || ''}
            isSelected={selectedIndex === index}
            isDrawFrom={drawFromIndex === index}
            onDragEnd={onWaypointDrag}
            onClick={onWaypointClick}
          />
        ))}

        {/* Mid-segment ghost marker — insert intermediate waypoint */}
        {midHover && (
          <Marker
            longitude={midHover.coord[0]}
            latitude={midHover.coord[1]}
            anchor="center"
          >
            <div
              className={styles.midpointGhost}
              title={`Insert waypoint between point ${midHover.afterIndex + 1} and ${midHover.afterIndex + 2}`}
              aria-label="Insert waypoint here"
            >
              +
            </div>
          </Marker>
        )}

        {/* ── Location finder pins (search field previews) ────────────────── */}
        {[
          startMarker && { coord: startMarker, bg: 'linear-gradient(160deg,#3b82f6,#1d4ed8)', shadow: '0 4px 14px rgba(29,78,216,0.40)', textColor: '#1d4ed8', pinText: 'A', key: 'finder-start', onDragEnd: handleStartMarkerDragEnd, title: 'Start — drag to reposition' },
          destMarker  && { coord: destMarker,  bg: 'linear-gradient(160deg,#f97316,#c2410c)', shadow: '0 4px 14px rgba(194,65,12,0.40)',  textColor: '#c2410c', pinText: 'B', key: 'finder-dest',  onDragEnd: handleDestMarkerDragEnd,  title: 'Destination — drag to reposition' },
        ].filter(Boolean).map(({ coord, bg, shadow, textColor, pinText, key, onDragEnd, title }) => (
          <Marker
            key={key}
            longitude={coord[0]}
            latitude={coord[1]}
            anchor="bottom"
            draggable
            onDragEnd={onDragEnd}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'grab' }}
              title={title}
              aria-label={title}
            >
              {/* Teardrop body — slightly smaller than route markers */}
              <div style={{
                position: 'relative',
                width: 38,
                height: 38,
                borderRadius: '50% 50% 50% 0',
                transform: 'rotate(-45deg)',
                background: bg,
                boxShadow: shadow,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {/* White circle inner */}
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: '#fff',
                  transform: 'rotate(45deg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: textColor,
                    lineHeight: 1,
                  }}>
                    {pinText}
                  </span>
                </div>
              </div>
            </div>
          </Marker>
        ))}

        {/* POI detail popup */}
        {selectedPoi && (
          <Popup
            longitude={selectedPoi.lng}
            latitude={selectedPoi.lat}
            anchor="bottom"
            offset={14}
            closeButton={false}
            closeOnClick={false}
            onClose={() => setSelectedPoi(null)}
            className={styles.poiPopupWrapper}
          >
            <div className={styles.poiPopup}>
              <button
                type="button"
                className={styles.poiPopupClose}
                onClick={() => setSelectedPoi(null)}
                aria-label="Close"
              >✕</button>
              <div className={styles.poiPopupHeader}>
                <span
                  className={styles.poiPopupIcon}
                  style={{ backgroundColor: getPoiTypeColor(selectedPoi.type) }}
                  aria-hidden="true"
                >
                  <img src={selectedPoiIconSrc} alt="" className={styles.poiPopupIconImg} />
                </span>
                <div>
                  <p className={styles.poiPopupName}>{selectedPoi.name}</p>
                  <span className={styles.poiPopupType}>{meta.label}</span>
                </div>
              </div>
              {selectedPoi.description && (
                <p className={styles.poiPopupDesc}>{selectedPoi.description}</p>
              )}
              {selectedPoi.amenities && selectedPoi.amenities.length > 0 && (
                <div className={styles.poiPopupAmenities}>
                  {selectedPoi.amenities.map((a) => (
                    <span key={a} className={styles.poiAmenityTag}>{a}</span>
                  ))}
                </div>
              )}
              {(selectedPoi.phone || selectedPoi.website) && (
                <div className={styles.poiPopupLinks}>
                  {selectedPoi.phone && (
                    <a href={`tel:${selectedPoi.phone}`} className={styles.poiPopupLink}>
                      📞 {selectedPoi.phone}
                    </a>
                  )}
                  {selectedPoi.website && (
                    <a href={selectedPoi.website} target="_blank" rel="noopener noreferrer" className={styles.poiPopupLink}>
                      🌐 Website
                    </a>
                  )}
                </div>
              )}
            </div>
          </Popup>
        )}
      </Map>

      {/* ── Bottom hint ─────────────────────────────────────────────────── */}
      <div className={`${styles.mapHint} ${drawFromIndex !== null ? styles.mapHintDrawFrom : ''}`} aria-live="polite">
        {waypoints.length === 0 ? (
          <><span className={styles.hintIcon}>🖱</span>Click the map to add stops</>
        ) : drawFromIndex !== null ? (
          <>
            <span className={styles.hintIcon}>✏️</span>
            Drawing from Stop&nbsp;
            <strong>{getStopNumber(drawFromIndex)}</strong>
            &nbsp;— click map to add next stop &nbsp;·&nbsp;
            <button type="button" className={styles.cancelDrawBtn} onClick={onCancelDrawFrom}>
              Cancel (Esc)
            </button>
          </>
        ) : midHover ? (
          <><span className={styles.hintIcon}>➕</span>Click to insert between stop {midHover.afterIndex + 1} and {midHover.afterIndex + 2}</>
        ) : (
          <><span className={styles.hintIcon}>📍</span>{waypoints.length} stop{waypoints.length !== 1 ? 's' : ''} — click map to add · hover line to insert</>
        )}
      </div>
    </div>
  );
};

export default RouteMap;
